import React, { useState } from 'react'
import {
  Box,
  VStack,
  Flex,
  Text,
  Icon,
  useColorModeValue,
  Button,
  Divider,
  Stack,
  Collapse,
  Image,
  Link,
} from '@chakra-ui/react'
import {
  FaDatabase,
  FaUser,
  FaPlus,
  FaSignOutAlt,
  FaChevronDown,
  FaChevronRight,
  FaShieldAlt,
  FaBook
} from 'react-icons/fa'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import { truncateAddress } from '../utils/helpers'
import logo from '../assets/logo.png'
import { adminService } from '../services/api'
import { useQuery } from '@tanstack/react-query'

interface SidebarProps {
  walletAddress?: string
  onLogout: () => void
}

interface NavItemProps {
  icon: React.ElementType
  children: React.ReactNode
  to: string
  hasSubNav?: boolean
  isOpen?: boolean
  onToggle?: () => void
}

interface SubNavItemProps {
  children: React.ReactNode
  to: string
}

const NavItem: React.FC<NavItemProps> = ({
  icon,
  children,
  to,
  hasSubNav = false,
  isOpen = false,
  onToggle,
  ...rest
}) => {
  const location = useLocation()
  const isActive =
    location.pathname === to || location.pathname.startsWith(to + '/')
  const activeBg = useColorModeValue('blue.50', 'blue.900')
  const activeColor = useColorModeValue('blue.700', 'blue.200')
  const inactiveColor = useColorModeValue('gray.600', 'gray.200')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')

  return (
    <Box width="100%">
      {hasSubNav ? (
        <Flex
          align="center"
          p="4"
          mx="4"
          borderRadius="lg"
          role="group"
          cursor="pointer"
          bg={isActive ? activeBg : 'transparent'}
          color={isActive ? activeColor : inactiveColor}
          fontWeight={isActive ? 'bold' : 'normal'}
          _hover={{
            bg: hoverBg,
            color: activeColor,
          }}
          onClick={onToggle}
          justify="space-between"
          {...rest}
        >
          <Flex align="center">
            <Icon mr="4" fontSize="16" as={icon} />
            {children}
          </Flex>
          <Icon
            as={isOpen ? FaChevronDown : FaChevronRight}
            transition="all .25s ease-in-out"
          />
        </Flex>
      ) : (
        <NavLink to={to} style={{ width: '100%' }}>
          <Flex
            align="center"
            p="4"
            mx="4"
            borderRadius="lg"
            role="group"
            cursor="pointer"
            bg={isActive ? activeBg : 'transparent'}
            color={isActive ? activeColor : inactiveColor}
            fontWeight={isActive ? 'bold' : 'normal'}
            _hover={{
              bg: hoverBg,
              color: activeColor,
            }}
            {...rest}
          >
            <Icon mr="4" fontSize="16" as={icon} />
            {children}
          </Flex>
        </NavLink>
      )}
    </Box>
  )
}

const SubNavItem: React.FC<SubNavItemProps> = ({ children, to, ...rest }) => {
  const location = useLocation()
  // Make Data tab active by default when at root data path
  const isActive =
    location.pathname === to ||
    (to === '/data/data' &&
      (location.pathname === '/data' || location.pathname === '/'))
  const activeBg = useColorModeValue('blue.50', 'blue.900')
  const activeColor = useColorModeValue('blue.700', 'blue.200')
  const inactiveColor = useColorModeValue('gray.600', 'gray.200')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')

  return (
    <NavLink to={to} style={{ width: '100%' }}>
      <Flex
        align="center"
        p="3"
        pl="12"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        bg={isActive ? activeBg : 'transparent'}
        color={isActive ? activeColor : inactiveColor}
        fontWeight={isActive ? 'bold' : 'normal'}
        fontSize="sm"
        _hover={{
          bg: hoverBg,
          color: activeColor,
        }}
        {...rest}
      >
        {children}
      </Flex>
    </NavLink>
  )
}

const Sidebar: React.FC<SidebarProps> = ({ walletAddress, onLogout }) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Default to open if on a data path, or if first visiting the app
  const [isRecordsOpen, setIsRecordsOpen] = useState(
    location.pathname.startsWith('/data') || location.pathname === '/'
  )

  // If we're at the root /data path or just /, redirect to /data/data
  React.useEffect(() => {
    if (location.pathname === '/data' || location.pathname === '/') {
      navigate('/data/data')
    }
  }, [location.pathname, navigate])

  const bg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')

  const toggleRecords = () => {
    setIsRecordsOpen(!isRecordsOpen)
  }

  const handleCreate = () => {
    navigate('/record/data/create')
  }

  
  const { data } = useQuery({
    queryKey: ['user_id'],
    queryFn: async () => {
      const response = await adminService.check()
      return response.data || []
    },
  })

  return (
    <Box
      as="nav"
      pos="fixed"
      top="0"
      left="0"
      h="full"
      w="60"
      bg={bg}
      borderRight="1px"
      borderRightColor={borderColor}
      paddingTop="4"
      overflowY="auto"
    >
      <Flex
        direction="column"
        as="nav"
        fontSize="sm"
        color="gray.600"
        aria-label="Main Navigation"
        height="full"
      >
        <Box px="4" py="5">
          <Image src={logo} alt="logo" />

          {walletAddress && (
            <Text fontSize="sm" color="gray.500" mt="1" textAlign={'center'}>
              {truncateAddress(walletAddress)}
            </Text>
          )}
        </Box>

        <Divider />

        <VStack spacing="0" align="stretch" flex="1">
          <NavItem
            icon={FaDatabase}
            to="/data"
            hasSubNav={true}
            isOpen={isRecordsOpen}
            onToggle={toggleRecords}
          >
            Records
          </NavItem>

          <Collapse in={isRecordsOpen} animateOpacity>
            <Box pb={2}>
              <SubNavItem to="/record/data">Data</SubNavItem>
              <SubNavItem to="/record/alpha">Alpha</SubNavItem>
            </Box>
          </Collapse>

          <NavItem icon={FaUser} to="/profile">
            Profile
          </NavItem>

          {
            data?.is_admin && (
              <NavItem icon={FaShieldAlt} to="/whitelist">
                Whitelist
              </NavItem>
            )
          }

          <Link href="https://capybaralabs-1.gitbook.io/liberal-alpha" isExternal style={{ width: '100%' }}>
            <Flex
              align="center"
              p="4"
              mx="4"
              borderRadius="lg"
              role="group"
              cursor="pointer"
              color={useColorModeValue('gray.600', 'gray.200')}
              _hover={{
                bg: useColorModeValue('gray.100', 'gray.700'),
                color: useColorModeValue('blue.700', 'blue.200'),
              }}
            >
              <Icon mr="4" fontSize="16" as={FaBook} />
              User Guide
            </Flex>
          </Link>
        </VStack>

        <Stack p="4" gap={3}>
          <Button
            colorScheme="pink"
            leftIcon={<Icon as={FaPlus} />}
            mr={3}
            onClick={handleCreate}
            size="sm"
            width="full"
          >
            Create Record
          </Button>

          <Divider />
          <Button
            leftIcon={<FaSignOutAlt />}
            variant="outline"
            colorScheme="red"
            size="sm"
            width="full"
            onClick={onLogout}
          >
            Logout
          </Button>
        </Stack>
      </Flex>
    </Box>
  )
}

export default Sidebar
